import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntdApp } from 'antd';
import { trpc, trpcClient } from './lib/api';
import { AuthGuard } from './components/auth/AuthGuard';
import { SubscriptionExpiredGuard } from './components/auth/SubscriptionExpiredGuard';
import { SharedUserGuard } from './components/auth/SharedUserGuard';
import { AdminAuthGuard } from './components/admin/AdminAuthGuard';
import { PermissionGuard } from './components/auth/PermissionGuard';
import { MaintenanceGuard } from './components/MaintenanceGuard';
import { PublicOnlyGuard } from './components/auth/PublicOnlyGuard';
import { MainLayout } from './layouts/MainLayout';
import { SharedUserLayout } from './layouts/SharedUserLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { SettingsLayout } from './layouts/SettingsLayout';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ServicePlansPage } from './pages/public/ServicePlansPage';
import { ServiceDetailPage } from './pages/public/ServiceDetailPage';
import { ContactPage } from './pages/public/ContactPage';
import EmailPreferencesEntryPage from './pages/public/EmailPreferencesEntryPage';
import { LogoProposalPage } from './pages/LogoProposalPage';
import { TermsPrivacyPage } from './pages/public/TermsPrivacyPage';
import { SpecifiedCommercialTransactionsPage } from './pages/public/SpecifiedCommercialTransactionsPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { MaintenanceLayoutMockPage } from './pages/MaintenanceLayoutMockPage';  
import { BuildingMaintenanceMockLayout } from './pages/building-maintenance-mock/BuildingMaintenanceMockLayout';
import { BuildingMaintenanceDashboardPage } from './pages/building-maintenance-mock/BuildingMaintenanceDashboardPage';
import { BuildingMaintenanceAreasPage } from './pages/building-maintenance-mock/BuildingMaintenanceAreasPage';
import { BuildingMaintenanceInspectionsPage } from './pages/building-maintenance-mock/BuildingMaintenanceInspectionsPage';
import { LocationsPageWithAPI } from './pages/LocationsPageWithAPI';
import { EquipmentsPage } from './pages/EquipmentsPage';
import { EquipmentDetailPage } from './pages/EquipmentDetailPage';
import { DeteriorationPageRouter } from './pages/DeteriorationPageRouter';
import { DeteriorationDetailPage } from './pages/DeteriorationDetailPage';
import { NotificationsPage } from './pages/NotificationsPage';
import ProfileManagement from './components/user/ProfileManagement';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { MaintenanceCalendarPage } from './pages/Maintenance/Calendar';
import ScheduleDetailPage from './pages/ScheduleDetailPage';
import WorkHistoryIndexPage from './pages/WorkHistory';
import WorkHistoryDetailPage from './pages/WorkHistory/Detail';
import EmergencyWorkPage from './pages/EmergencyWorkPage';
import { EmergencyWorkDetailPage } from './pages/EmergencyWorkDetailPage';
import { ImportPage } from './pages/ImportPage';
import { ExportPage } from './pages/ExportPage';
import { ImportExportPage } from './pages/ImportExportPage';
import CompanyInfoPage from './pages/settings/CompanyInfoPage';
import BranchManagementPage from './pages/settings/BranchManagementPage';
import SubscriptionSettingsPage from './pages/settings/SubscriptionSettingsPage';
import WithdrawPage from './pages/settings/WithdrawPage';
import SettingsDashboardPage from './pages/settings/SettingsDashboardPage';
import UserManagementPage from './pages/UserManagementPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { PartsPage } from './pages/PartsPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { SupplierDetailPage } from './pages/SupplierDetailPage';
import { PartDetailPage } from './pages/PartDetailPage';
import { StockDashboardPage } from './pages/StockDashboardPage';
import { PurchaseOrdersPage } from './pages/PurchaseOrdersPage';
import { PurchaseOrderCategoryOrdersPage } from './pages/PurchaseOrderCategoryOrdersPage';
import { CreatePurchaseOrderPage } from './pages/CreatePurchaseOrderPage';
import { PurchaseOrderDetailPage } from './pages/PurchaseOrderDetailPage';
import { PurchaseOrderPdfOutputPage } from './pages/PurchaseOrderPdfOutputPage';
import { PurchaseOrderPrintPage } from './pages/PurchaseOrderPrintPage';
import { PartsAnalyticsPage } from './pages/PartsAnalyticsPage';
import { PartConsumptionLogsPage } from './pages/PartConsumptionLogsPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { OfficialInspectionDocumentsPage } from './pages/OfficialInspectionDocumentsPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminUserManagementPage } from './pages/admin/auth/AdminUserManagementPage';
import { AuditLogViewerPage } from './pages/admin/auth/AuditLogViewerPage';
import { DevToolsPage } from './pages/admin/DevToolsPage';
import { UserActivityPage } from './pages/admin/UserActivityPage';
import { UserListPage } from './pages/admin/users/UserListPage';
import { UserDetailPage } from './pages/admin/users/UserDetailPage';
import { CompanyListPage } from './pages/admin/companies/CompanyListPage';
import { CompanyDetailPage } from './pages/admin/companies/CompanyDetailPage';
import { SubscriptionListPage } from './pages/admin/subscriptions/SubscriptionListPage';
import { PlanMasterPage } from './pages/admin/subscriptions/PlanMasterPage';
import BankTransferManagementPage from './pages/admin/subscriptions/BankTransferManagementPage';
import CouponManagementPage from './pages/admin/subscriptions/CouponManagementPage';
import { AccessControlTemplatesPage } from './pages/admin/settings/AccessControlTemplatesPage';
import BankTransferConfirmationPage from './pages/subscription/BankTransferConfirmationPage';
import AddonPurchasePage from './pages/subscription/AddonPurchasePage';
import NotificationSettingsPage from './pages/settings/NotificationSettingsPage';
import { WorkOrderTemplatesPage } from './pages/WorkOrderTemplates/WorkOrderTemplatesPage';
import { WorkOrderTemplateDetailPage } from './pages/WorkOrderTemplates/WorkOrderTemplateDetailPage';
import { SystemConfigPage } from './pages/settings/SystemConfigPage';
import AdvancedSettingsPage from './pages/settings/AdvancedSettingsPage';
import RecurringSettingsPage from './pages/Maintenance/RecurringSettings';
import CategoriesPage from './pages/settings/CategoriesPage';
import { QrLabelPrintPage } from './pages/QrLabelPrintPage';
import MyTasksPage from './pages/MyTasksPage';
import { StorageManagementPage } from './pages/StorageManagement';
import IndustryGuidesPage from './pages/IndustryGuidesPage';
import IndustryGuideDetailPage from './pages/IndustryGuideDetailPage';
import DemoAccountsPage from './pages/DemoAccountsPage';
import PasswordResetPage from './components/user/PasswordResetPage';
import { HelpPage } from './pages/HelpPage';
import { HelpArticlePage } from './pages/HelpArticlePage';
import { PageMemoSettingsPage } from './pages/settings/PageMemoSettingsPage';

import AnnouncementListPage from './pages/admin/notifications/AnnouncementListPage';
import { MaintenanceModePage } from './pages/admin/settings/MaintenanceModePage';
import { MaintenancePage } from './pages/MaintenancePage';
import { adminBasePath, adminLoginPath, adminPath } from './lib/adminPaths';

// 代理店関連
import { AgencyLoginPage } from './pages/agency/AgencyLoginPage';
import { AgencySetupPage } from './pages/agency/AgencySetupPage';
import { AgencyDashboardPage } from './pages/agency/AgencyDashboardPage';
import { AgencyCouponsPage } from './pages/agency/AgencyCouponsPage';
import { AgencyCommissionsPage } from './pages/agency/AgencyCommissionsPage';
import { AgencyProfilePage } from './pages/agency/AgencyProfilePage';
import { AgencyLayout } from './layouts/AgencyLayout';
import { AgencyAuthGuard } from './components/auth/AgencyAuthGuard';
import { PartnerLoginPage } from './pages/partner/PartnerLoginPage';
import { PartnerSetupPage } from './pages/partner/PartnerSetupPage';
import { PartnerDashboardPage } from './pages/partner/PartnerDashboardPage';
import { PartnerCompaniesPage } from './pages/partner/PartnerCompaniesPage';
import { PartnerCompanyCreatePage } from './pages/partner/PartnerCompanyCreatePage';
import { PartnerCompanyDetailPage } from './pages/partner/PartnerCompanyDetailPage';
import { PartnerRequestsPage } from './pages/partner/PartnerRequestsPage';
import { PartnerUsersPage } from './pages/partner/PartnerUsersPage';
import { PartnerLayout } from './layouts/PartnerLayout';
import { PartnerAuthGuard } from './components/auth/PartnerAuthGuard';
import { UserSetupPage } from './pages/UserSetupPage';
import InvitationAcceptPage from './components/user/InvitationAcceptPage';

// 管理画面 - 代理店管理
import { AgencyListPage } from './pages/admin/agencies/AgencyListPage';
import { AgencyDetailPage } from './pages/admin/agencies/AgencyDetailPage';
import { AgencyCommissionManagementPage } from './pages/admin/agencies/AgencyCommissionManagementPage';
import { PartnerListPage } from './pages/admin/partners/PartnerListPage';
import { PartnerDetailPage } from './pages/admin/partners/PartnerDetailPage';
import { featureFlags } from './utils/featureFlags';
import { applyEnvironmentFavicon } from './constants/environment';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <MaintenanceGuard>
          <PublicOnlyGuard>
            <LandingPage />
          </PublicOnlyGuard>
        </MaintenanceGuard>
      ),
    },
    {
      path: '/login',
      element: <MaintenanceGuard><LoginPage /></MaintenanceGuard>,
    },
    {
      path: '/register',
      element: (
        <MaintenanceGuard>
          <PublicOnlyGuard>
            <RegisterPage />
          </PublicOnlyGuard>
        </MaintenanceGuard>
      ),
    },
    {
      path: '/maintenance',
      element: <MaintenancePage />,
    },
    {
      path: '/auth/password-reset',
      element: <MaintenanceGuard><PasswordResetPage /></MaintenanceGuard>,
    },
    {
      path: '/invite/accept',
      element: <MaintenanceGuard><InvitationAcceptPage /></MaintenanceGuard>,
    },
    {
      path: '/app/invite/accept',
      element: <MaintenanceGuard><InvitationAcceptPage /></MaintenanceGuard>,
    },
    {
      path: '/reset-password',
      element: <MaintenanceGuard><PasswordResetPage /></MaintenanceGuard>,
    },
    {
      path: '/terms',
      element: <Navigate to="/legal/terms-privacy" replace />,
    },
    {
      path: '/industry-guides',
      element: <IndustryGuidesPage />,
    },
    {
      path: '/industry-guides/:industryCode',
      element: <IndustryGuideDetailPage />,
    },
    {
      path: '/help',
      element: <HelpPage />,
    },
    {
      path: '/help/:articleId',
      element: <HelpArticlePage />,
    },
    {
      path: '/demo-accounts',
      element: <MaintenanceGuard><DemoAccountsPage /></MaintenanceGuard>,
    },
    {
      path: '/service/plans',
      element: <ServicePlansPage />,
    },
    {
      path: '/service/features',
      element: <ServiceDetailPage />,
    },
    {
      path: '/contact',
      element: <ContactPage />,
    },
    {
      path: '/account/email-preferences',
      element: <EmailPreferencesEntryPage />,
    },
    {
      path: '/logo-proposals',
      element: <LogoProposalPage />,
    },
    {
      path: '/app/maintenance-ui-mock',
      element: (
        <MaintenanceGuard>
          <AuthGuard>
            <SubscriptionExpiredGuard>
              <MaintenanceLayoutMockPage />
            </SubscriptionExpiredGuard>
          </AuthGuard>
        </MaintenanceGuard>
      ),
    },
    {
      path: '/app/building-maintenance-mock',
      element: (
        <MaintenanceGuard>
          <AuthGuard>
            <SubscriptionExpiredGuard>
              <BuildingMaintenanceMockLayout />
            </SubscriptionExpiredGuard>
          </AuthGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <BuildingMaintenanceDashboardPage />,
        },
        {
          path: 'areas',
          element: <BuildingMaintenanceAreasPage />,
        },
        {
          path: 'inspections',
          element: <BuildingMaintenanceInspectionsPage />,
        },
      ],
    },
    {
      path: '/legal/terms-privacy',
      element: <TermsPrivacyPage />,
    },
    {
      path: '/legal/specified-commercial-transactions',
      element: <SpecifiedCommercialTransactionsPage />,
    },
    {
      path: '/app',
      element: (
        <MaintenanceGuard>
          <AuthGuard>
            <SubscriptionExpiredGuard>
              <MainLayout />
            </SubscriptionExpiredGuard>
          </AuthGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/app/dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <DashboardPage />,
        },
        {
          path: 'locations',
          element: <LocationsPageWithAPI />,
        },
        {
          path: 'branches',
          element: <BranchManagementPage />,
        },
        {
          path: 'equipments',
          element: <EquipmentsPage />,
        },
        {
          path: 'equipments/:id',
          element: <EquipmentDetailPage />,
        },
        {
          path: 'equipments/:id/edit',
          element: <div>設備編集（準備中）</div>,
        },
        {
          path: 'deterioration',
          element: <DeteriorationPageRouter />,
        },
        {
          path: 'deterioration/:id',
          element: <DeteriorationDetailPage />,
        },
        {
          path: 'maintenance',
          element: <MaintenanceCalendarPage />
        },
        {
          path: 'maintenance/recurring-settings',
          element: <RecurringSettingsPage />
        },
        {
          path: 'maintenance/:id',
          element: <ScheduleDetailPage />,
        },
        {
          path: 'schedule/:id',
          element: <ScheduleDetailPage />,
        },
        {
          path: 'my-tasks',
          element: <MyTasksPage />
        },
        {
          path: 'work-order-management',
          element: <Navigate to="/app/maintenance" replace />,
        },
        {
          path: 'work-order',
          element: <Navigate to="/app/maintenance" replace />,
        },
        {
          path: 'work-order/:id',
          element: <Navigate to="/app/work-history" replace />,
        },
        {
          path: 'work-orders',
          element: <Navigate to="/app/work-history" replace />,
        },
        {
          path: 'work-orders/:id',
          element: <Navigate to="/app/work-history" replace />,
        },
        {
          path: 'work-order-templates',
          element: <WorkOrderTemplatesPage />,
        },
        {
          path: 'work-order-templates/:id',
          element: <WorkOrderTemplateDetailPage />,
        },
        {
          path: 'work-history',
          element: <WorkHistoryIndexPage />,
        },
        {
          path: 'work-history/:id',
          element: <WorkHistoryDetailPage />,
        },
        {
          path: 'emergency-works',
          element: <EmergencyWorkPage />,
        },
        {
          path: 'emergency-works/:id',
          element: <EmergencyWorkDetailPage />,
        },
        {
          path: 'import',
          element: <ImportPage />,
        },
        {
          path: 'export',
          element: <Navigate to="/app/settings/export" replace />,
        },
        {
          path: 'import-export',
          element: <ImportExportPage />,
        },
        {
          path: 'announcements',
          element: <AnnouncementsPage />,
        },
        {
          path: 'parts',
          element: <PartsPage />,
        },
        {
          path: 'parts/:id',
          element: <PartDetailPage />,
        },
        {
          path: 'suppliers',
          element: <SuppliersPage />,
        },
        {
          path: 'suppliers/:id',
          element: <SupplierDetailPage />,
        },
        {
          path: 'documents',
          element: <DocumentsPage />,
        },
        {
          path: 'documents/:id',
          element: <DocumentDetailPage />,
        },
        {
          path: 'documents/official-inspections',
          element: <OfficialInspectionDocumentsPage />,
        },
        {
          path: 'stock-dashboard',
          element: <StockDashboardPage />,
        },
        {
          path: 'purchase-orders',
          element: <PurchaseOrdersPage />,
        },
        {
          path: 'purchase-orders/category-orders',
          element: <PurchaseOrderCategoryOrdersPage />,
        },
        {
          path: 'purchase-orders/category-orders/:categoryId',
          element: <PurchaseOrderCategoryOrdersPage />,
        },
        {
          path: 'purchase-orders/create',
          element: <CreatePurchaseOrderPage />,
        },
        {
          path: 'purchase-orders/:id/pdf',
          element: <PurchaseOrderPdfOutputPage />,
        },
        {
          path: 'purchase-orders/:id',
          element: <PurchaseOrderDetailPage />,
        },
        {
          path: 'parts-analytics',
          element: <PartsAnalyticsPage />,
        },
        {
          path: 'part-consumption-logs',
          element: <PartConsumptionLogsPage />,
        },
        {
          path: 'notifications',
          element: <NotificationsPage />,
        },
        {
          path: 'memos',
          element: <PageMemoSettingsPage />,
        },
        {
          path: 'profile/notifications',
          element: <NotificationSettingsPage />,
        },
        // 旧URLからのリダイレクト
        {
          path: 'settings',
          element: <Navigate to="/app/settings/company" replace />,
        },
        {
          path: 'settings/*',
          element: <Navigate to="/app/settings/company" replace />,
        },
        {
          path: 'categories',
          element: <CategoriesPage />,
        },
        {
          path: 'qr-labels',
          element: featureFlags.qrLabelPrinting
            ? <QrLabelPrintPage />
            : <Navigate to="/app/dashboard" replace />,
        },
        {
          path: 'subscription',
          element: <Navigate to="/app/settings/subscription" replace />,
        },
        {
          path: 'subscription/bank-transfer/confirmation',
          element: (import.meta.env.VITE_ENABLE_SUBSCRIPTION_FEATURES === 'false' || !featureFlags.bankTransfer)
            ? <Navigate to="/app/dashboard" replace />
            : <BankTransferConfirmationPage />,
        },
        {
          path: 'subscription/addon',
          element: import.meta.env.VITE_ENABLE_SUBSCRIPTION_FEATURES === 'false'
            ? <Navigate to="/app/dashboard" replace />
            : <AddonPurchasePage />,
        },
        {
          path: 'user-management',
          element: <Navigate to="/app/settings/users" replace />,
        },
        {
          path: 'import',
          element: <Navigate to="/app/settings/import" replace />,
        },
        {
          path: 'export',
          element: <Navigate to="/app/settings/export" replace />,
        },
        {
          path: 'profile',
          element: <ProfileManagement />,
        },
      ],
    },
    {
      path: '/print/purchase-orders/:id',
      element: (
        <MaintenanceGuard>
          <AuthGuard>
            <SubscriptionExpiredGuard>
              <PurchaseOrderPrintPage />
            </SubscriptionExpiredGuard>
          </AuthGuard>
        </MaintenanceGuard>
      ),
    },
    // 設定ページ専用レイアウト
    {
      path: '/app/settings',
      element: (
        <MaintenanceGuard>
          <AuthGuard>
            <SubscriptionExpiredGuard>
              <SettingsLayout />
            </SubscriptionExpiredGuard>
          </AuthGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <SettingsDashboardPage />,
        },
        {
          path: 'dashboard',
          element: <SettingsDashboardPage />,
        },
        {
          path: 'company',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <CompanyInfoPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'branches',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <BranchManagementPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'system-config',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <SystemConfigPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'advanced',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <AdvancedSettingsPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'memos',
          element: <Navigate to="/app/memos" replace />,
        },
        {
          path: 'storage',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <StorageManagementPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'users',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <UserManagementPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'subscription',
          element: import.meta.env.VITE_ENABLE_SUBSCRIPTION_FEATURES === 'false'
            ? <Navigate to="/app/dashboard" replace />
            : (
              <PermissionGuard requiredRoles={['admin', 'manager']}>
                <SubscriptionSettingsPage />
              </PermissionGuard>
            ),
        },
        {
          path: 'import',
          element: (
            <PermissionGuard requiredRoles={['admin', 'manager']}>
              <ImportPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'export',
          element: (
            <PermissionGuard
              requiredRoles={['admin']}
              denySubtitle="このページを表示するには管理者権限が必要です。"
            >
              <ExportPage />
            </PermissionGuard>
          ),
        },
        {
          path: 'withdraw',
          element: <WithdrawPage />,
        },
      ],
    },
    // 共有ユーザー専用ルート
    {
      path: '/shared-app',
      element: (
        <MaintenanceGuard>
          <SharedUserGuard>
            <SharedUserLayout />
          </SharedUserGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/shared-app/dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <DashboardPage />,
        },
        {
          path: 'maintenance',
          element: <MaintenanceCalendarPage />
        },
        {
          path: 'maintenance/:id',
          element: <ScheduleDetailPage />,
        },
        {
          path: 'work-history',
          element: <WorkHistoryIndexPage />,
        },
        {
          path: 'work-history/:id',
          element: <WorkHistoryDetailPage />,
        },
        {
          path: 'emergency-works',
          element: <EmergencyWorkPage />,
        },
        {
          path: 'emergency-works/:id',
          element: <EmergencyWorkDetailPage />,
        },
        {
          path: 'my-tasks',
          element: <MyTasksPage />
        },
      ],
    },
    // 代理店ルート
    {
      path: '/agency/login',
      element: <MaintenanceGuard><AgencyLoginPage /></MaintenanceGuard>,
    },
    {
      path: '/agency/setup',
      element: <MaintenanceGuard><AgencySetupPage /></MaintenanceGuard>,
    },
    {
      path: '/auth/setup',
      element: <MaintenanceGuard><UserSetupPage /></MaintenanceGuard>,
    },
    {
      path: '/agency',
      element: (
        <MaintenanceGuard>
          <AgencyAuthGuard>
            <AgencyLayout />
          </AgencyAuthGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/agency/dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <AgencyDashboardPage />,
        },
        {
          path: 'coupons',
          element: <AgencyCouponsPage />,
        },
        {
          path: 'commissions',
          element: <AgencyCommissionsPage />,
        },
        {
          path: 'profile',
          element: <AgencyProfilePage />,
        },
      ],
    },
    // パートナールート
    {
      path: '/partner/login',
      element: <MaintenanceGuard><PartnerLoginPage /></MaintenanceGuard>,
    },
    {
      path: '/partner/setup',
      element: <MaintenanceGuard><PartnerSetupPage /></MaintenanceGuard>,
    },
    {
      path: '/partner',
      element: (
        <MaintenanceGuard>
          <PartnerAuthGuard>
            <PartnerLayout />
          </PartnerAuthGuard>
        </MaintenanceGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/partner/dashboard" replace />,
        },
        {
          path: 'dashboard',
          element: <PartnerDashboardPage />,
        },
        {
          path: 'companies',
          element: <PartnerCompaniesPage />,
        },
        {
          path: 'companies/new',
          element: <PartnerCompanyCreatePage />,
        },
        {
          path: 'companies/:companyId',
          element: <PartnerCompanyDetailPage />,
        },
        {
          path: 'requests',
          element: <PartnerRequestsPage />,
        },
        {
          path: 'users',
          element: <PartnerUsersPage />,
        },
      ],
    },
    // 管理画面ルート
    {
      path: adminLoginPath,
      element: <AdminLoginPage />,
    },
    {
      path: adminBasePath,
      element: (
        <AdminAuthGuard>
          <AdminLayout />
        </AdminAuthGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to={adminPath('dashboard')} replace />,
        },
        {
          path: 'dashboard',
          element: <AdminDashboard />,
        },
        {
          path: 'companies',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'company_admin', 'customer_success']} requireScope="assigned">
              <CompanyListPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'companies/:companyId',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'company_admin', 'customer_success']} requireScope="assigned">
              <CompanyDetailPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'subscriptions',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance', 'company_admin']} requireScope="company">
              <SubscriptionListPage />
            </AdminAuthGuard>
          ),
        },
        // 銀行振込管理はフラグで制御
        ...(featureFlags.bankTransfer ? [{
          path: 'subscriptions/bank-transfers',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance', 'company_admin']} requireScope="company">
              <BankTransferManagementPage />
            </AdminAuthGuard>
          ),
        }] : [{
          path: 'subscriptions/bank-transfers',
          element: <Navigate to={adminPath('dashboard')} replace />,
        }]),
        {
          path: 'subscriptions/plan-master',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance']} requireScope="all">
              <PlanMasterPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'subscriptions/coupons',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance']} requireScope="all">
              <CouponManagementPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'agencies',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance', 'agency_manager']} requireScope="all">
              <AgencyListPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'agencies/:id',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance', 'agency_manager']} requireScope="all">
              <AgencyDetailPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'agencies/commissions',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'finance', 'agency_manager']} requireScope="all">
              <AgencyCommissionManagementPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'partners',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'agency_manager']} requireScope="all">
              <PartnerListPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'partners/:id',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'agency_manager']} requireScope="all">
              <PartnerDetailPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'announcements',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'customer_success', 'company_admin']} requireScope="company">
              <AnnouncementListPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'users',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'company_admin']} requireScope="company">
              <UserListPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'users/:id',
          element: (
            <AdminAuthGuard requireRoles={['system_admin', 'company_admin']} requireScope="company">
              <UserDetailPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'settings/access-templates',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <AccessControlTemplatesPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'settings/maintenance',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <MaintenanceModePage />
            </AdminAuthGuard>
          ),
        },
        // 管理ユーザー管理
        {
          path: 'admin-users',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <AdminUserManagementPage />
            </AdminAuthGuard>
          ),
        },
        // 監査ログ
        {
          path: 'audit-log',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <AuditLogViewerPage />
            </AdminAuthGuard>
          ),
        },
        // 開発環境専用ツール（本番ではアクセス不可）
        {
          path: 'dev-tools',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <DevToolsPage />
            </AdminAuthGuard>
          ),
        },
        {
          path: 'user-activity',
          element: (
            <AdminAuthGuard requireRoles={['system_admin']} requireScope="all">
              <UserActivityPage />
            </AdminAuthGuard>
          ),
        },
      ],
    },
  ]
);

function App() {
  useEffect(() => {
    applyEnvironmentFavicon();
  }, []);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <UserPreferencesProvider>
          <GoogleAnalytics />
          <AntdApp>
            <RouterProvider router={router} />
          </AntdApp>
        </UserPreferencesProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

export default App;


